/**
 * CanvasSelection.tsx
 * Composant pour gérer le rectangle de sélection (marquee)
 * Extrait de CanvasArea.tsx pour améliorer la maintenabilité
 */
import React from 'react';
import { Rect } from 'react-konva';
import type { ShapeData } from '../../../types/ShapeTypes';

export interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CanvasSelectionProps {
  selectionRect: SelectionRect | null;
  /** Position (pan) du stage, pour convertir les coords écran → contenu. */
  stagePos?: { x: number; y: number };
  /** Échelle (zoom) du stage. */
  stageScale?: number;
}

/**
 * Composant pour afficher le rectangle de sélection.
 *
 * FIX : `selectionRect` est en pixels ÉCRAN (issu de `getPointerPosition()`),
 * mais ce composant est rendu DANS le Stage transformé (scale + pan). Sans
 * conversion, le rectangle était doublement transformé → décalé/mal dimensionné
 * dès qu'on zoomait ou pannait. On convertit donc en coordonnées de contenu du
 * stage, et on neutralise le scale sur le trait pour garder une bordure nette.
 */
export const CanvasSelection: React.FC<CanvasSelectionProps> = ({
  selectionRect,
  stagePos = { x: 0, y: 0 },
  stageScale = 1,
}) => {
  if (!selectionRect) {
    return null;
  }

  const s = stageScale || 1;
  const x = (selectionRect.x - stagePos.x) / s;
  const y = (selectionRect.y - stagePos.y) / s;
  const width = selectionRect.width / s;
  const height = selectionRect.height / s;

  return (
    <>
      {/* Rectangle de sélection avec style amélioré */}
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(59, 130, 246, 0.15)"
        stroke="#3b82f6"
        strokeWidth={2 / s}
        dash={[8 / s, 4 / s]}
        listening={false}
        shadowBlur={4 / s}
        shadowColor="rgba(59, 130, 246, 0.3)"
        shadowOffsetX={0}
        shadowOffsetY={2 / s}
      />
      {/* Bordure intérieure pour meilleure visibilité */}
      <Rect
        x={x + 1 / s}
        y={y + 1 / s}
        width={Math.max(0, width - 2 / s)}
        height={Math.max(0, height - 2 / s)}
        fill="transparent"
        stroke="rgba(255, 255, 255, 0.5)"
        strokeWidth={1 / s}
        dash={[4 / s, 4 / s]}
        listening={false}
      />
    </>
  );
};

/**
 * Helper pour calculer les shapes qui intersectent le rectangle de sélection
 */
export function getShapesInSelectionRect(
  selectionRect: SelectionRect,
  shapes: ShapeData[],
  stagePos: { x: number; y: number },
  stageScale: number,
  selectionOffset: { x: number; y: number }
): string[] {
  // Convertir les coordonnées du rectangle de sélection en coordonnées du stage
  const rectInStage = {
    x: (selectionRect.x - stagePos.x) / stageScale,
    y: (selectionRect.y - stagePos.y) / stageScale,
    width: selectionRect.width / stageScale,
    height: selectionRect.height / stageScale,
  };

  const intersects = (shape: ShapeData) => {
    const sx = (shape.x || 0) + selectionOffset.x;
    const sy = (shape.y || 0) + selectionOffset.y;
    const sw = shape.width || 100;
    const sh = shape.height || 100;
    const rx = rectInStage.x;
    const ry = rectInStage.y;
    const rw = rectInStage.width;
    const rh = rectInStage.height;
    const shapeRight = sx + sw;
    const shapeBottom = sy + sh;
    const rectRight = rx + rw;
    const rectBottom = ry + rh;
    return !(shapeRight < rx || sx > rectRight || shapeBottom < ry || sy > rectBottom);
  };

  return shapes.filter(intersects).map(s => s.id);
}
